import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import Loading from '../../components/Loading';
import { useAuth } from '../../contexts/AuthProvider';
import AuditOutputTemplatePreview from '../../components/AuditOutputTemplatePreview';
import {
  AUDIT_OUTPUT_SITE_MODES,
  AUDIT_OUTPUT_TYPES,
  buildDefaultAuditOutputTemplate,
} from '../../utils/auditOutputTemplateDefaults';
import {
  CERTIFICATE_FIELD_KEYS,
  normalizeCertificateCoordinateLayout,
  nudgeCoordinateField,
  updateCoordinateField,
} from '../../utils/certificateCoordinateLayout';
import {
  buildAuditOutputRuntimeData,
  parseFormDataEntry,
} from '../../utils/auditOutputTemplateData';
import { buildAuditOutputPdfPreview, downloadAuditOutputPdf } from '../../utils/auditOutputTemplatePdf';
import {
  createAuditOutputTemplate,
  deleteAuditOutputTemplate,
  listAuditOutputTemplates,
  updateAuditOutputTemplate,
} from '../../services/auditOutputTemplateService';
import { getStandards, loadDocumentFormData } from '../../services/auditFindingsService';
import api from '../../api';
import './AuditOutputTemplate.css';

/** Matches NavLinks `canViewModule` / SalesReport capability module key */
const AUDIT_OUTPUT_TEMPLATE_MODULE = 'audit-output-template';

const OUTPUT_TYPE_TO_DOC_CODE = {
  certificate: 'FTRD-02',
  report: 'Audit Summary Report',
  opinion: 'FTRD-01',
};

const CERTIFICATE_BASE_TEMPLATES = [
  { value: 'auto', label: 'Auto by Standard' },
  { value: 'isms-2026', label: 'ISO/IEC 27001 - ISMS (2026)' },
  { value: 'isms-v3', label: 'ISO/IEC 27001 - ISMS (v3)' },
  { value: 'qms-accredited-2026', label: 'ISO 9001 - QMS Accredited (2026)' },
  { value: 'qms-2026', label: 'ISO 9001 - QMS (2026)' },
  { value: 'qms-v3', label: 'ISO 9001 - QMS (v3)' },
  { value: 'qms-nonaccredited-v3', label: 'ISO 9001 - QMS Non Accredited (v3)' },
  { value: '14001-2026', label: 'ISO 14001 - EMS (2026)' },
  { value: '14001-v3', label: 'ISO 14001 - EMS (v3)' },
  { value: '45001-v3', label: 'ISO 45001 - OHSMS' },
  { value: '27701-2026', label: 'ISO/IEC 27701 - PIMS (2026)' },
  { value: '27701-v3', label: 'ISO/IEC 27701 - PIMS (v3)' },
  { value: '37001-2026', label: 'ISO 37001 - ABMS (2026)' },
  { value: 'abms-v4', label: 'ISO 37001 - ABMS (v4)' },
  { value: '22301-v3', label: 'ISO 22301 - BCMS' },
  { value: '23257-v3', label: 'SNI/ISO 23257 - BDLT' },
  { value: 'iscs-v3', label: 'ISO/IEC 27017 - ISCS' },
  { value: 'ispp-v3', label: 'ISO/IEC 27018 - ISPP' },
  { value: 'pms-v3', label: 'ISO 21500 - PMS' },
];

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const TokenHint = () => (
  <div className="audit-output-token-hint">
    <strong>Merge tokens:</strong>{' '}
    <code>{'{{project.companyName}}'}</code>, <code>{'{{project.projectCode}}'}</code>,{' '}
    <code>{'{{certificate.certificateNumber}}'}</code>, <code>{'{{certificate.scope}}'}</code>,{' '}
    <code>{'{{certificate.standards}}'}</code>, <code>{'{{certificate.issueDate}}'}</code>,{' '}
    <code>{'{{certificate.expiredDate}}'}</code>, <code>{'{{certificate.signatureTitle}}'}</code>,{' '}
    <code>{'{{report.summaryNarrative}}'}</code>,{' '}
    <code>{'{{report.conclusion}}'}</code>, <code>{'{{page.current}}'}</code>,{' '}
    <code>{'{{page.total}}'}</code>
  </div>
);

const Field = ({ label, children, hint }) => (
  <div className="audit-output-field">
    <label className="form-label">
      {label}
      {hint ? <span className="audit-output-field-hint">{hint}</span> : null}
    </label>
    {children}
  </div>
);

const AuditOutputTemplate = () => {
  const [searchParams] = useSearchParams();
  const requestedType = searchParams.get('type');
  const { can, hasRole, capabilitiesLoading } = useAuth();
  const isAdmin = hasRole('ROLE_ADMIN');
  const isDirector = hasRole('ROLE_DIRECTOR');

  const canViewPage =
    isAdmin ||
    isDirector ||
    can('view_own', AUDIT_OUTPUT_TEMPLATE_MODULE) ||
    can('view_department', AUDIT_OUTPUT_TEMPLATE_MODULE) ||
    can('view_global', AUDIT_OUTPUT_TEMPLATE_MODULE);
  const canEditTemplates = isAdmin || isDirector || can('edit', AUDIT_OUTPUT_TEMPLATE_MODULE);
  const canDeleteTemplates = isAdmin || isDirector || can('delete', AUDIT_OUTPUT_TEMPLATE_MODULE);
  const canExportPdf = isAdmin || isDirector || can('export_data', AUDIT_OUTPUT_TEMPLATE_MODULE);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [templateDraft, setTemplateDraft] = useState(() => buildDefaultAuditOutputTemplate({
    outputType: AUDIT_OUTPUT_TYPES.some((item) => item.value === requestedType) ? requestedType : 'certificate',
  }));
  const [projects, setProjects] = useState([]);
  const [previewProjectId, setPreviewProjectId] = useState('');
  const [previewContext, setPreviewContext] = useState(null);
  const [standards, setStandards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedCoordField, setSelectedCoordField] = useState(null);

  const activeType = templateDraft.outputType;
  const typeLabel = AUDIT_OUTPUT_TYPES.find((item) => item.value === activeType)?.label || 'Output';

  const selectedTemplate = useMemo(
    () => templates.find((item) => String(item.id) === String(selectedTemplateId)) || null,
    [templates, selectedTemplateId],
  );

  useEffect(() => {
    if (capabilitiesLoading || !canViewPage) return;
    let mounted = true;
    const loadInitialData = async () => {
      setLoading(true);
      setError('');
      try {
        const [templateList, projectsResponse, standardList] = await Promise.all([
          listAuditOutputTemplates(),
          api.get('/crm/projects'),
          getStandards().catch(() => []),
        ]);
        if (!mounted) return;

        const projectItems = Array.isArray(projectsResponse?.data)
          ? projectsResponse.data
          : projectsResponse?.data?.content || projectsResponse?.data?.data || [];

        setTemplates(templateList);
        setProjects(projectItems);
        setStandards(Array.isArray(standardList) ? standardList : []);

        if (templateList.length > 0) {
          const matching = requestedType
            ? templateList.find((item) => item.outputType === requestedType)
            : templateList[0];
          setSelectedTemplateId(matching?.id || templateList[0]?.id || null);
          setTemplateDraft(buildDefaultAuditOutputTemplate(matching || {}));
        }

        if (projectItems.length > 0) {
          setPreviewProjectId(String(projectItems[0].id));
        }
      } catch (requestError) {
        if (!mounted) return;
        setError(requestError?.response?.data?.message || 'Failed to load audit output templates.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadInitialData();
    return () => {
      mounted = false;
    };
  }, [requestedType, capabilitiesLoading, canViewPage]);

  useEffect(() => {
    if (capabilitiesLoading || !canViewPage || !previewProjectId) return;
    let mounted = true;

    const loadPreviewData = async () => {
      setPreviewLoading(true);
      try {
        const project = projects.find((item) => String(item.id) === String(previewProjectId)) || null;
        const [contactsRes, formEntries, summaryRes] = await Promise.all([
          api.get(`/audit/projects/${previewProjectId}/contacts`).catch(() => ({ data: null })),
          loadDocumentFormData(previewProjectId).catch(() => []),
          api.get(`/audit/findings/projects/${previewProjectId}/summary`).catch(() => ({ data: null })),
        ]);

        if (!mounted) return;

        const formMap = {};
        (Array.isArray(formEntries) ? formEntries : []).forEach((entry) => {
          if (entry?.documentCode) {
            formMap[entry.documentCode] = parseFormDataEntry(entry);
          }
        });

        setPreviewContext({
          project,
          contacts: contactsRes?.data || {},
          certificateForm: formMap['FTRD-02'] || {},
          certificateInfoForm: formMap['FAPP-06'] || {},
          summaryData: summaryRes?.data?.data || null,
          siteLocations: (formMap['AUDIT_PROCESS_SCHEDULING']?.siteLocations || []).filter(Boolean),
          auditSchedules: formMap['AUDIT_PROCESS_SCHEDULING']?.auditSchedules || [],
        });
      } finally {
        if (mounted) setPreviewLoading(false);
      }
    };

    loadPreviewData();
    return () => {
      mounted = false;
    };
  }, [previewProjectId, projects, capabilitiesLoading, canViewPage]);

  useEffect(() => {
    if (!selectedTemplate) return;
    setTemplateDraft(buildDefaultAuditOutputTemplate(selectedTemplate));
  }, [selectedTemplate]);

  const runtimeData = useMemo(() => buildAuditOutputRuntimeData({
    template: templateDraft,
    project: previewContext?.project || {},
    projectContacts: previewContext?.contacts || {},
    certificateForm: previewContext?.certificateForm || {},
    certificateInfoForm: previewContext?.certificateInfoForm || {},
    summaryData: previewContext?.summaryData,
    siteLocations: previewContext?.siteLocations || [],
    auditSchedules: previewContext?.auditSchedules || [],
    selectedScopeLabel: previewContext?.certificateForm?.certificateScope || '',
  }), [previewContext, templateDraft]);

  const updateDraft = (field, value) => {
    setTemplateDraft((prev) => ({ ...prev, [field]: value }));
  };

  const updateConfigSection = (section, field, value) => {
    setTemplateDraft((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        [section]: {
          ...prev.config[section],
          [field]: value,
        },
      },
    }));
  };

  const updateCertificateDate = (index, field, value) => {
    setTemplateDraft((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        certificate: {
          ...prev.config.certificate,
          dates: prev.config.certificate.dates.map((item, itemIndex) => (
            itemIndex === index ? { ...item, [field]: value } : item
          )),
        },
      },
    }));
  };

  const updateCertificateCoordinateLayout = (layout) => {
    updateDraft('config', {
      ...templateDraft.config,
      certificate: {
        ...templateDraft.config.certificate,
        coordinateLayout: layout,
      },
    });
  };

  const coordinateLayout = normalizeCertificateCoordinateLayout(templateDraft.config.certificate.coordinateLayout);

  const handleSelectTemplate = (template) => {
    setSelectedTemplateId(template?.id || null);
    setTemplateDraft(buildDefaultAuditOutputTemplate(template || {
      outputType: requestedType || 'certificate',
    }));
    setSuccessMessage('');
    setError('');
  };

  const handleCreateNew = () => {
    if (!canEditTemplates) {
      setError('You do not have permission to create templates.');
      return;
    }
    setSelectedTemplateId(null);
    setTemplateDraft(buildDefaultAuditOutputTemplate({
      outputType: requestedType || templateDraft.outputType || 'certificate',
    }));
    setSuccessMessage('');
    setError('');
  };

  const handleToggleStandard = (standard) => {
    setTemplateDraft((prev) => ({
      ...prev,
      standards: prev.standards.includes(standard)
        ? prev.standards.filter((item) => item !== standard)
        : [...prev.standards, standard],
    }));
  };

  const handleFileUpload = async (target, file) => {
    if (!file) return;
    const dataUrl = await readFileAsDataUrl(file);
    if (target === 'logo') {
      updateConfigSection('logo', 'dataUrl', dataUrl);
      updateConfigSection('logo', 'enabled', true);
      return;
    }
    if (target === 'certificate-signature') {
      updateDraft('config', {
        ...templateDraft.config,
        certificate: {
          ...templateDraft.config.certificate,
          signatureImage: {
            ...templateDraft.config.certificate.signatureImage,
            dataUrl,
            enabled: true,
          },
        },
      });
      return;
    }
    updateConfigSection('page', 'backgroundImage', dataUrl);
  };

  const handleSave = async () => {
    if (!canEditTemplates) {
      setError('You do not have permission to save templates.');
      return;
    }
    if (!templateDraft.templateName.trim()) {
      setError('Template name is required.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const payload = {
        templateName: templateDraft.templateName.trim(),
        description: templateDraft.description?.trim() || '',
        outputType: templateDraft.outputType,
        siteMode: templateDraft.siteMode,
        standards: templateDraft.standards,
        config: templateDraft.config,
      };
      const saved = selectedTemplateId
        ? await updateAuditOutputTemplate(selectedTemplateId, payload)
        : await createAuditOutputTemplate(payload);
      const refreshed = await listAuditOutputTemplates();
      setTemplates(refreshed);
      setSelectedTemplateId(saved.id);
      setTemplateDraft(buildDefaultAuditOutputTemplate(saved));
      setSuccessMessage('Template saved successfully.');
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canDeleteTemplates) {
      setError('You do not have permission to delete templates.');
      return;
    }
    if (!selectedTemplateId) return;
    if (!window.confirm('Delete this master output template?')) return;

    setSaving(true);
    setError('');
    try {
      await deleteAuditOutputTemplate(selectedTemplateId);
      const refreshed = await listAuditOutputTemplates();
      setTemplates(refreshed);
      if (refreshed.length > 0) {
        handleSelectTemplate(refreshed[0]);
      } else {
        handleCreateNew();
      }
      setSuccessMessage('Template deleted.');
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Failed to delete template.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewPdf = async () => {
    if (!canExportPdf) {
      setError('You do not have permission to preview or export PDF.');
      return;
    }
    const { url } = await buildAuditOutputPdfPreview(templateDraft, runtimeData);
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const handleDownloadPdf = async () => {
    if (!canExportPdf) {
      setError('You do not have permission to download PDF.');
      return;
    }
    const suffix = previewContext?.project?.projectCode || typeLabel.toLowerCase();
    await downloadAuditOutputPdf(templateDraft, runtimeData, `${templateDraft.templateName || typeLabel}-${suffix}.pdf`);
  };

  // Open backend-generated certificate PDF (only for certificate output type)
  const handleViewCertificatePdf = () => {
    if (!previewProjectId) {
      setError('Please select a project to generate the certificate PDF.');
      return;
    }
    const params = selectedTemplateId ? `?templateId=${encodeURIComponent(selectedTemplateId)}` : '';
    const url = `/api/audit/certificates/project/${previewProjectId}/preview${params}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const relevantDocCode = OUTPUT_TYPE_TO_DOC_CODE[activeType];

  if (capabilitiesLoading) {
    return <Loading />;
  }

  if (!canViewPage) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="audit-output-template-loading">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="audit-output-template-shell">
      <aside className="audit-output-template-sidebar">
        <div className="audit-output-template-sidebar-header">
          <div>
            <h5>Master Audit Output</h5>
            <p>Reusable template library for certificate, report, and opinion drafts.</p>
          </div>
          {canEditTemplates ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={handleCreateNew}>
              Create
            </button>
          ) : null}
        </div>

        <div className="audit-output-template-list">
          {templates.length === 0 ? (
            <div className="audit-output-template-empty">
              <strong>No templates yet.</strong>
              <span>Create the first master output template to use in audit process.</span>
            </div>
          ) : templates.map((template) => (
            <button
              type="button"
              key={template.id}
              className={`audit-output-template-list-item${String(template.id) === String(selectedTemplateId) ? ' is-active' : ''}`}
              onClick={() => handleSelectTemplate(template)}
            >
              <strong>{template.templateName}</strong>
              <span>{AUDIT_OUTPUT_TYPES.find((item) => item.value === template.outputType)?.label}</span>
              <small>
                {template.siteMode === 'multi-site' ? 'Multi Site' : 'Single Site'}
                {template.standards?.length ? ` • ${template.standards.join(', ')}` : ''}
              </small>
            </button>
          ))}
        </div>
      </aside>

      <main className="audit-output-template-main">
        <div className="audit-output-template-toolbar">
          <div>
            <h4>{selectedTemplateId ? 'Edit Master Template' : 'Create Master Template'}</h4>
            <p>This template will feed {typeLabel.toLowerCase()} draft preview/download in audit process.</p>
          </div>
          <div className="audit-output-template-toolbar-actions">
            {searchParams.get('returnTo') ? (
              <Link className="btn btn-outline-secondary btn-sm" to={searchParams.get('returnTo')}>
                Back to Audit Process
              </Link>
            ) : null}
            {selectedTemplateId && canDeleteTemplates ? (
              <button type="button" className="btn btn-outline-danger btn-sm" onClick={handleDelete} disabled={saving}>
                Delete
              </button>
            ) : null}
            {canExportPdf ? (
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={handlePreviewPdf}>
                Preview PDF
              </button>
            ) : null}
            {canExportPdf && activeType === 'certificate' ? (
              <button
                type="button"
                className="btn btn-sm"
                style={{ background: '#1f656c', color: '#fff', border: 'none' }}
                onClick={handleViewCertificatePdf}
                title={previewProjectId ? 'Generate certificate PDF from backend for selected project' : 'Select a project first'}
              >
                🏆 Generate Certificate PDF
              </button>
            ) : null}
            {canEditTemplates ? (
              <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Template'}
              </button>
            ) : null}
          </div>
        </div>

        {error ? <div className="alert alert-danger mb-3">{error}</div> : null}
        {successMessage ? <div className="alert alert-success mb-3">{successMessage}</div> : null}

        <div className="audit-output-template-grid">
          <section className="audit-output-template-editor">
            <fieldset
              disabled={!canEditTemplates}
              className="border-0 p-0 m-0"
              style={{ minWidth: 0 }}
            >
            <div className="card mb-3">
              <div className="card-body">
                <h6 className="card-title">Template Setup</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <Field label="Template Name">
                      <input
                        className="form-control"
                        value={templateDraft.templateName}
                        onChange={(event) => updateDraft('templateName', event.target.value)}
                        placeholder="e.g. ISO Certificate Multisite"
                      />
                    </Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Output Type">
                      <select
                        className="form-select"
                        value={templateDraft.outputType}
                        onChange={(event) => updateDraft('outputType', event.target.value)}
                      >
                        {AUDIT_OUTPUT_TYPES.map((item) => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Site Mode">
                      <select
                        className="form-select"
                        value={templateDraft.siteMode}
                        onChange={(event) => updateDraft('siteMode', event.target.value)}
                      >
                        {AUDIT_OUTPUT_SITE_MODES.map((item) => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Linked Draft Form">
                      <input className="form-control" value={relevantDocCode} readOnly />
                    </Field>
                  </div>
                  {activeType === 'certificate' ? (
                    <div className="col-md-6">
                      <Field label="Certificate Base Template" hint="Default follows selected standard">
                        <select
                          className="form-select"
                          value={templateDraft.config.certificate.baseTemplateKey || 'auto'}
                          onChange={(event) => updateConfigSection('certificate', 'baseTemplateKey', event.target.value)}
                        >
                          {CERTIFICATE_BASE_TEMPLATES.map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                          ))}
                        </select>
                      </Field>
                    </div>
                  ) : null}
                  <div className="col-12">
                    <Field label="Description">
                      <textarea
                        className="form-control"
                        rows={2}
                        value={templateDraft.description}
                        onChange={(event) => updateDraft('description', event.target.value)}
                        placeholder="When should the audit team use this template?"
                      />
                    </Field>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-body">
                <h6 className="card-title">Applicable Standards</h6>
                <div className="audit-output-standard-grid">
                  {standards.length === 0 ? (
                    <div className="text-muted small">No master standards available from backend.</div>
                  ) : standards.map((standard) => (
                    <label key={standard} className="audit-output-standard-option">
                      <input
                        type="checkbox"
                        checked={templateDraft.standards.includes(standard)}
                        onChange={() => handleToggleStandard(standard)}
                      />
                      <span>{standard}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-body">
                <h6 className="card-title">Page Styling & Assets</h6>
                <div className="row g-3">
                  <div className="col-md-4">
                    <Field label="Background Color">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={templateDraft.config.page.backgroundColor}
                        onChange={(event) => updateConfigSection('page', 'backgroundColor', event.target.value)}
                      />
                    </Field>
                  </div>
                  <div className="col-md-4">
                    <Field label="Text Color">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={templateDraft.config.page.textColor}
                        onChange={(event) => updateConfigSection('page', 'textColor', event.target.value)}
                      />
                    </Field>
                  </div>
                  <div className="col-md-4">
                    <Field label="Accent Color">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={templateDraft.config.page.accentColor}
                        onChange={(event) => updateConfigSection('page', 'accentColor', event.target.value)}
                      />
                    </Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Background Image" hint="Used behind page content">
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={(event) => handleFileUpload('background', event.target.files?.[0])}
                      />
                    </Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Logo" hint="Drag on preview to reposition">
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={(event) => handleFileUpload('logo', event.target.files?.[0])}
                      />
                    </Field>
                  </div>
                  <div className="col-md-3">
                    <Field label="Logo Width (mm)">
                      <input
                        type="number"
                        className="form-control"
                        value={templateDraft.config.logo.width}
                        onChange={(event) => updateConfigSection('logo', 'width', Number(event.target.value) || 0)}
                      />
                    </Field>
                  </div>
                  <div className="col-md-3">
                    <Field label="Logo Height (mm)">
                      <input
                        type="number"
                        className="form-control"
                        value={templateDraft.config.logo.height}
                        onChange={(event) => updateConfigSection('logo', 'height', Number(event.target.value) || 0)}
                      />
                    </Field>
                  </div>
                  <div className="col-md-3">
                    <Field label="Padding Top (mm)">
                      <input
                        type="number"
                        className="form-control"
                        value={templateDraft.config.page.paddingTop}
                        onChange={(event) => updateConfigSection('page', 'paddingTop', Number(event.target.value) || 0)}
                      />
                    </Field>
                  </div>
                  <div className="col-md-3">
                    <Field label="Padding Bottom (mm)">
                      <input
                        type="number"
                        className="form-control"
                        value={templateDraft.config.page.paddingBottom}
                        onChange={(event) => updateConfigSection('page', 'paddingBottom', Number(event.target.value) || 0)}
                      />
                    </Field>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-3">
              <div className="card-body">
                <h6 className="card-title">Header & Footer</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <Field label="Header Title">
                      <input
                        className="form-control"
                        value={templateDraft.config.header.title}
                        onChange={(event) => updateConfigSection('header', 'title', event.target.value)}
                      />
                    </Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Header Subtitle">
                      <input
                        className="form-control"
                        value={templateDraft.config.header.subtitle}
                        onChange={(event) => updateConfigSection('header', 'subtitle', event.target.value)}
                      />
                    </Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Footer Left">
                      <input
                        className="form-control"
                        value={templateDraft.config.footer.leftText}
                        onChange={(event) => updateConfigSection('footer', 'leftText', event.target.value)}
                      />
                    </Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Footer Right">
                      <input
                        className="form-control"
                        value={templateDraft.config.footer.rightText}
                        onChange={(event) => updateConfigSection('footer', 'rightText', event.target.value)}
                      />
                    </Field>
                  </div>
                </div>
              </div>
            </div>

            {activeType === 'certificate' && (
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="card-title">Certificate Content</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Field label="Title">
                        <input className="form-control" value={templateDraft.config.certificate.title} onChange={(event) => updateConfigSection('certificate', 'title', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-md-6">
                      <Field label="Subtitle">
                        <input className="form-control" value={templateDraft.config.certificate.subtitle} onChange={(event) => updateConfigSection('certificate', 'subtitle', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-12">
                      <Field label="Statement">
                        <textarea className="form-control" rows={2} value={templateDraft.config.certificate.statement} onChange={(event) => updateConfigSection('certificate', 'statement', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-md-6">
                      <Field label="Company Name">
                        <input className="form-control" value={templateDraft.config.certificate.companyName} onChange={(event) => updateConfigSection('certificate', 'companyName', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-md-6">
                      <Field label="Company Address">
                        <input className="form-control" value={templateDraft.config.certificate.companyAddress} onChange={(event) => updateConfigSection('certificate', 'companyAddress', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-12">
                      <Field label="Standards Intro">
                        <textarea className="form-control" rows={2} value={templateDraft.config.certificate.standardsIntro} onChange={(event) => updateConfigSection('certificate', 'standardsIntro', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-12">
                      <Field label="Standards Value">
                        <input className="form-control" value={templateDraft.config.certificate.standardsText} onChange={(event) => updateConfigSection('certificate', 'standardsText', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-md-6">
                      <Field label="Scope Label">
                        <input className="form-control" value={templateDraft.config.certificate.scopeLabel} onChange={(event) => updateConfigSection('certificate', 'scopeLabel', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-md-6">
                      <Field label="Verification Label">
                        <input className="form-control" value={templateDraft.config.certificate.verificationLabel} onChange={(event) => updateConfigSection('certificate', 'verificationLabel', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-12">
                      <Field label="Scope Content">
                        <textarea className="form-control" rows={2} value={templateDraft.config.certificate.scopeText} onChange={(event) => updateConfigSection('certificate', 'scopeText', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-12">
                      <Field label="Verification Content">
                        <textarea className="form-control" rows={2} value={templateDraft.config.certificate.verificationText} onChange={(event) => updateConfigSection('certificate', 'verificationText', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-md-6">
                      <Field label="Certificate Number Label">
                        <input className="form-control" value={templateDraft.config.certificate.certificateNumberLabel} onChange={(event) => updateConfigSection('certificate', 'certificateNumberLabel', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-md-6">
                      <Field label="Decision Label">
                        <input className="form-control" value={templateDraft.config.certificate.decisionLabel} onChange={(event) => updateConfigSection('certificate', 'decisionLabel', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-md-6">
                      <Field label="Signature Name">
                        <input className="form-control" value={templateDraft.config.certificate.signatureName} onChange={(event) => updateConfigSection('certificate', 'signatureName', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-md-6">
                      <Field label="Signature Title">
                        <input className="form-control" value={templateDraft.config.certificate.signatureTitle} onChange={(event) => updateConfigSection('certificate', 'signatureTitle', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-md-6">
                      <Field label="Signature Image" hint="Displayed above signer name in preview/PDF">
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={(event) => handleFileUpload('certificate-signature', event.target.files?.[0])}
                        />
                      </Field>
                    </div>
                    <div className="col-md-3">
                      <Field label="Signature Width (mm)">
                        <input
                          type="number"
                          className="form-control"
                          value={templateDraft.config.certificate.signatureImage.width}
                          onChange={(event) => updateDraft('config', {
                            ...templateDraft.config,
                            certificate: {
                              ...templateDraft.config.certificate,
                              signatureImage: {
                                ...templateDraft.config.certificate.signatureImage,
                                width: Number(event.target.value) || 0,
                              },
                            },
                          })}
                        />
                      </Field>
                    </div>
                    <div className="col-md-3">
                      <Field label="Signature Height (mm)">
                        <input
                          type="number"
                          className="form-control"
                          value={templateDraft.config.certificate.signatureImage.height}
                          onChange={(event) => updateDraft('config', {
                            ...templateDraft.config,
                            certificate: {
                              ...templateDraft.config.certificate,
                              signatureImage: {
                                ...templateDraft.config.certificate.signatureImage,
                                height: Number(event.target.value) || 0,
                              },
                            },
                          })}
                        />
                      </Field>
                    </div>
                    <div className="col-md-6">
                      <Field label="Signature Image Options">
                        <div className="form-check form-switch mt-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="certificate-signature-enabled"
                            checked={!!templateDraft.config.certificate.signatureImage.enabled}
                            onChange={(event) => updateDraft('config', {
                              ...templateDraft.config,
                              certificate: {
                                ...templateDraft.config.certificate,
                                signatureImage: {
                                  ...templateDraft.config.certificate.signatureImage,
                                  enabled: event.target.checked,
                                },
                              },
                            })}
                          />
                          <label className="form-check-label" htmlFor="certificate-signature-enabled">
                            Show signature image in certificate draft
                          </label>
                        </div>
                        <div className="form-check form-switch mt-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="certificate-signature-ratio"
                            checked={templateDraft.config.certificate.signatureImage.preserveAspectRatio !== false}
                            onChange={(event) => updateDraft('config', {
                              ...templateDraft.config,
                              certificate: {
                                ...templateDraft.config.certificate,
                                signatureImage: {
                                  ...templateDraft.config.certificate.signatureImage,
                                  preserveAspectRatio: event.target.checked,
                                },
                              },
                            })}
                          />
                          <label className="form-check-label" htmlFor="certificate-signature-ratio">
                            Preserve original signature ratio
                          </label>
                        </div>
                      </Field>
                    </div>
                    <div className="col-12">
                      <Field label="Remarks / Notes">
                        <textarea className="form-control" rows={2} value={templateDraft.config.certificate.remarks} onChange={(event) => updateConfigSection('certificate', 'remarks', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-12">
                      <div className="audit-output-coordinate-editor">
                        <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
                          <div>
                            <h6 className="mb-1">Certificate Coordinate Editor</h6>
                            <p className="text-muted small mb-0">Drag fields directly in the preview to reposition. Resize using the corner handle. Click a field to see font size and alignment controls.</p>
                          </div>
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="certificate-coordinate-layout-enabled"
                              checked={coordinateLayout.enabled}
                              onChange={(event) => updateCertificateCoordinateLayout({
                                ...coordinateLayout,
                                enabled: event.target.checked,
                              })}
                            />
                            <label className="form-check-label" htmlFor="certificate-coordinate-layout-enabled">
                              Use coordinate layout
                            </label>
                          </div>
                        </div>
                        <div className="audit-output-coordinate-grid">
                          {CERTIFICATE_FIELD_KEYS.map(({ key, label }) => {
                            const field = coordinateLayout.fields[key];
                            return (
                              <div className={`audit-output-coordinate-row${key === selectedCoordField ? ' is-selected-row' : ''}`} key={key}>
                                <strong>{label}</strong>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={field.x}
                                  aria-label={`${label} X`}
                                  onChange={(event) => updateCertificateCoordinateLayout(updateCoordinateField(coordinateLayout, key, { x: Number(event.target.value) || 0 }))}
                                />
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={field.y}
                                  aria-label={`${label} Y`}
                                  onChange={(event) => updateCertificateCoordinateLayout(updateCoordinateField(coordinateLayout, key, { y: Number(event.target.value) || 0 }))}
                                />
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={field.width}
                                  aria-label={`${label} Width`}
                                  onChange={(event) => updateCertificateCoordinateLayout(updateCoordinateField(coordinateLayout, key, { width: Number(event.target.value) || 0 }))}
                                />
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  value={field.height}
                                  aria-label={`${label} Height`}
                                  onChange={(event) => updateCertificateCoordinateLayout(updateCoordinateField(coordinateLayout, key, { height: Number(event.target.value) || 0 }))}
                                />
                                <select
                                  className="form-select form-select-sm"
                                  value={field.align}
                                  aria-label={`${label} Align`}
                                  onChange={(event) => updateCertificateCoordinateLayout(updateCoordinateField(coordinateLayout, key, { align: event.target.value }))}
                                >
                                  <option value="left">Left</option>
                                  <option value="center">Center</option>
                                  <option value="right">Right</option>
                                </select>
                                <div className="btn-group btn-group-sm">
                                  <button type="button" className="btn btn-outline-secondary" onClick={() => updateCertificateCoordinateLayout(nudgeCoordinateField(coordinateLayout, key, 0, -1))}>Up</button>
                                  <button type="button" className="btn btn-outline-secondary" onClick={() => updateCertificateCoordinateLayout(nudgeCoordinateField(coordinateLayout, key, -1, 0))}>Left</button>
                                  <button type="button" className="btn btn-outline-secondary" onClick={() => updateCertificateCoordinateLayout(nudgeCoordinateField(coordinateLayout, key, 1, 0))}>Right</button>
                                  <button type="button" className="btn btn-outline-secondary" onClick={() => updateCertificateCoordinateLayout(nudgeCoordinateField(coordinateLayout, key, 0, 1))}>Down</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="audit-output-date-grid">
                    {templateDraft.config.certificate.dates.map((item, index) => (
                      <div key={`${item.label}-${index}`} className="audit-output-date-row">
                        <input
                          className="form-control"
                          value={item.label}
                          onChange={(event) => updateCertificateDate(index, 'label', event.target.value)}
                        />
                        <input
                          className="form-control"
                          value={item.value}
                          onChange={(event) => updateCertificateDate(index, 'value', event.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeType === 'report' && (
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="card-title">Report Content</h6>
                  <div className="row g-3">
                    {['title', 'subtitle', 'intro', 'body', 'conclusionTitle', 'conclusionText', 'recommendationTitle', 'recommendationText'].map((key) => (
                      <div className="col-12" key={key}>
                        <Field label={key}>
                          {key.toLowerCase().includes('title') || key === 'subtitle' ? (
                            <input className="form-control" value={templateDraft.config.report[key]} onChange={(event) => updateConfigSection('report', key, event.target.value)} />
                          ) : (
                            <textarea className="form-control" rows={2} value={templateDraft.config.report[key]} onChange={(event) => updateConfigSection('report', key, event.target.value)} />
                          )}
                        </Field>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeType === 'opinion' && (
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="card-title">Opinion Content</h6>
                  <div className="row g-3">
                    {['title', 'subtitle', 'intro', 'decisionLabel', 'decisionValue', 'body'].map((key) => (
                      <div className="col-12" key={key}>
                        <Field label={key}>
                          {key === 'body' || key === 'intro' ? (
                            <textarea className="form-control" rows={2} value={templateDraft.config.opinion[key]} onChange={(event) => updateConfigSection('opinion', key, event.target.value)} />
                          ) : (
                            <input className="form-control" value={templateDraft.config.opinion[key]} onChange={(event) => updateConfigSection('opinion', key, event.target.value)} />
                          )}
                        </Field>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {templateDraft.siteMode === 'multi-site' && (
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="card-title">Page 2+ Multi-Site Layout</h6>
                  <div className="alert alert-info py-2 small">
                    Site list is sourced from Audit Process &gt; Audit Scheduling &gt; Site Location for the selected project/scope.
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Field label="Page Title">
                        <input className="form-control" value={templateDraft.config.multiSite.title} onChange={(event) => updateConfigSection('multiSite', 'title', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-md-6">
                      <Field label="Footer Note">
                        <input className="form-control" value={templateDraft.config.multiSite.footerNote} onChange={(event) => updateConfigSection('multiSite', 'footerNote', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-12">
                      <Field label="Intro Text">
                        <textarea className="form-control" rows={2} value={templateDraft.config.multiSite.intro} onChange={(event) => updateConfigSection('multiSite', 'intro', event.target.value)} />
                      </Field>
                    </div>
                    <div className="col-12">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="multi-site-include-primary"
                          checked={templateDraft.config.multiSite.includePrimarySiteOnAppendix !== false}
                          onChange={(event) => updateConfigSection('multiSite', 'includePrimarySiteOnAppendix', event.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="multi-site-include-primary">
                          Include primary scheduled site on multi-site appendix
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            </fieldset>
            <TokenHint />
          </section>

          <section className="audit-output-template-preview-panel">
            <div className="card mb-3">
              <div className="card-body">
                <div className="audit-output-preview-toolbar">
                  <div>
                    <h6 className="card-title mb-1">Live Preview</h6>
                    <p className="text-muted small mb-0">Drag the logo directly on the page. Preview data is taken from an audit project.</p>
                  </div>
                  <div className="audit-output-preview-actions">
                    <select
                      className="form-select form-select-sm"
                      value={previewProjectId}
                      onChange={(event) => setPreviewProjectId(event.target.value)}
                    >
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.projectCode} - {project.companyName}
                        </option>
                      ))}
                    </select>
                    {canExportPdf ? (
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleDownloadPdf}>
                        Download PDF
                      </button>
                    ) : null}
                  </div>
                </div>

                {previewLoading ? (
                  <div className="audit-output-template-loading-inline">
                    <div className="spinner-border spinner-border-sm text-primary" role="status" />
                    <span>Loading project data...</span>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="audit-output-preview-stage">
              <AuditOutputTemplatePreview
                template={templateDraft}
                runtimeData={runtimeData}
                editableLogo={canEditTemplates}
                onLogoChange={(logo) => updateDraft('config', {
                  ...templateDraft.config,
                  logo,
                })}
                editableCoordinates={coordinateLayout.enabled}
                onCoordinateFieldChange={(key, patch) => {
                  updateCertificateCoordinateLayout(updateCoordinateField(coordinateLayout, key, patch));
                  setSelectedCoordField(key);
                }}
              />
              {templateDraft.siteMode === 'multi-site' && (runtimeData?.certificate?.sites || []).length > 1 ? (
                <AuditOutputTemplatePreview
                  template={templateDraft}
                  runtimeData={runtimeData}
                  renderSitePage
                />
              ) : null}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default AuditOutputTemplate;
